import {productSortRates} from '../features/erp/orderSort';
import {directMediaForm} from './mediaUpload';
import {prepareSupplierAvatar} from '../utils/productImage';
import type {ApiProductSale} from './erp';
import type { ImageVariants } from '../types/image';
import { teamFetch } from './teamTransport';
import { readQuery } from './queryCache';
import { ECOMMERCE_API_BASE_URL } from '../config';
import { apiErrorMessage, NETWORK_ERROR_MESSAGE, readErrorBody } from './errorMessage';
import { getSupplierToken, SupplierRole, SupplierInfo } from '../features/supplier/supplierAccess';

const BASE = `${ECOMMERCE_API_BASE_URL}/supplier`;

export class SupplierApiError extends Error {
    status: number;
    /** Parsed error body (e.g. `details` of a duplicate product). */
    body: unknown;
    constructor(message: string, status: number, body?: unknown) {
        super(message);
        this.name = 'SupplierApiError';
        this.status = status;
        this.body = body;
    }
}

export interface SupplierProduct extends ApiProductSale {
    can_copy_catalog?:boolean;can_move_catalog?:boolean;
    inventory_detail?: import('./erp').ApiProductDetail | null;
    _creation_token?:string;
    id: number;
    supplier_id: number;
    supplier_name?: string | null;
    /** 选入来源仅记录事实，不决定编辑权限。 */
    source?: 'supplier' | 'team' | 'scraper';
    maintenance?: 'supplier' | 'withpot';
    item_code: string;
    name: string | null;
    price: number | null;
    price_currency: string;
    height: number | null;
    width: number | null;
    depth: number | null;
    weight: number | null;
    category_name: string | null;
    product_labels: string[];
    product_note: string | null;
    brand_id: number | null;
    series_id?: number | null;
    brand_name: string | null;
    /** 服务端判定的采购完整性。品牌缺失会挡住接单，所以网页也要能看到。 */
    purchase_ready: boolean;
    missing_purchase_fields: string[];
    front_url: string | null;
    image_variants?:ImageVariants|null;
    bottom_url: string | null;
    collect_platform?: string | null;
    collect_url?: string | null;
    collect_sku?: string | null;
    status: string;
    // ── 商店 platform richer contract (Phase B) ──
    supply_price: number | null;
    retail_price: number | null;
    supplier_sku: string | null;
    purchase_url?: string | null;
    withpot_sku: string;
    /** Team readers only: the inventory product digitized from this pot. */
    product_master_id?:number|null;
    /** 历史实际入库件数，不随预留或售出减少。仅团队可读。 */
    received_purchase_quantity?:number;
    linked_product_master?: { id: number; sku: string } | null;
    internal_size_cm: { length: number | null; width: number | null; height: number | null };
    external_size_cm: { length: number | null; width: number | null; height: number | null };
    attribute_source?: 'brand'|'series'|'pot'|null;
    attribute_sources?: Record<string,'brand'|'series'|'pot'>;
    attributes: {
        drainage_hole?: string; saucer?: string; material?: string;
        ceramic_clay?: string; ceramic_glaze?: string; ceramic_crafts?: string[];
        selling_points?: string[]; description?: string;
    };
    detail_url: string | null;
    /** Detail-sized images (1600px). */
    photos: { front: string | null; detail: string | null; bottom: string | null };
    /** Grid previews (800px) — a fraction of the bytes, for list rendering. */
    thumbnails: { front: string | null; detail: string | null; bottom: string | null };
    client_id: string | null;
    revision: number;
    created_at: string | null;
    updated_at: string | null;
}

export type SupplierKind = 'supplier' | 'withpot';
export const SUPPLIER_KIND_LABELS: Record<SupplierKind,string> = {supplier:'由供应商维护',withpot:'由 WITHPOT 维护'};
export interface SupplierSummary extends SupplierInfo {
    english_name?:string|null;
    product_count:number;
    maintenance_mode:SupplierKind;
    supports_store_live?:boolean;
    login_account:string;
    initial_password?:string;
    apple_id?:string|null;
    _creation_token?:string;
    thumbnails?:string[];
    phone?:string|null;
    avatar_url?:string|null;
    wechat?:string|null;
    email?:string|null;
    suitable_plants?:string[];
    pot_style?:string|null;
    brand_story?:string|null;
    is_active?:boolean;
    location?:{country?:string|null;city?:string|null;country_code?:string|null;region?:string|null;provider?:string|null;place_id?:string|null};
    online_channel?:{type?:string|null;url?:string|null};
    payment?:{recipient?:string|null;account?:string|null;bank?:string|null;branch?:string|null;branch_code?:string|null};
    return_address?:{recipient?:string|null;phone?:string|null;address?:string|null;door_number?:string|null};
}
export type SupplierChanges = Partial<Omit<SupplierSummary,'id'|'login_account'|'initial_password'|'_creation_token'>>;

export interface LoginResult {
    token: string;
    role: SupplierRole;
    supplier: SupplierInfo | null;
}

function authHeaders(extra?: Record<string, string>): Record<string, string> {
    const token = getSupplierToken();
    return { ...(token ? { Authorization: `Bearer ${token}` } : {}), ...extra };
}

function doFetch(input: string, init?: RequestInit): Promise<Response> {
    return teamFetch(input, init, () => new SupplierApiError(NETWORK_ERROR_MESSAGE, 0));
}

async function handle<T>(res: Response): Promise<T> {
    if (!res.ok) {
        const body = await readErrorBody(res);
        throw new SupplierApiError(apiErrorMessage(res.status, body), res.status, body);
    }
    return res.json();
}

// ── Auth ────────────────────────────────────────────────────────────────────
// ERP team authentication; suppliers use the App.
export interface ProductBrand { id: number; name: string }

/** 共享的品牌字典。App 录品和网页货盘写的是同一张表。 */
export async function listProductBrands(signal?:AbortSignal): Promise<ProductBrand[]> {
    const res = await doFetch(`${BASE}/brands`, { headers: authHeaders(), signal });
    return (await handle<{ brands: ProductBrand[] }>(res)).brands;
}

export async function listProductSeries(brandId:number|string,signal?:AbortSignal):Promise<string[]> {
    const res=await doFetch(`${BASE}/product-series?${typeof brandId==='number'?'brand_id':'brand_name'}=${encodeURIComponent(brandId)}`,{headers:authHeaders(),signal});
    return (await handle<{series:string[]}>(res)).series;
}

export interface CatalogName { name:string; brandId?:number; aliases?:string[]; can_delete:boolean; delete_disabled_reason:string|null }
export async function listCatalogNames(brandId?:number,signal?:AbortSignal,supplierId?:number):Promise<CatalogName[]> {
    const series=brandId!=null;
    const path=series?'product-series':'brands';
    const res=await doFetch(`${BASE}/${path}?view=management${series?`&brand_id=${brandId}`:''}${supplierId==null?'':`&supplier_id=${supplierId}`}`,{headers:authHeaders(),signal});
    const result=await handle<{series?:CatalogName[];brands?:(CatalogName&{id:number})[]}>(res);
    return series?result.series??[]:(result.brands??[]).map(({id,...row})=>({...row,brandId:id}));
}
export async function renameCatalogName(row:CatalogName,name:string,brandId?:number):Promise<{name:string}> {
    const path=brandId==null?'brands':'product-series';
    const query=brandId==null?'':`?brand_id=${brandId}`;
    return handle(await doFetch(`${BASE}/${path}/name${query}`,{method:'PATCH',headers:authHeaders({'Content-Type':'application/json'}),
        body:JSON.stringify({name:row.name,new_name:name,...(brandId==null?{}:{aliases:row.aliases??[row.name]})})}));
}
export async function deleteCatalogNames(names:string[],brandId?:number):Promise<void> {
    const path=brandId==null?'brands':'product-series';
    const query=brandId==null?'':`?brand_id=${brandId}`;
    await handle(await doFetch(`${BASE}/${path}/batch-delete${query}`,{method:'POST',
        headers:authHeaders({'Content-Type':'application/json'}),body:JSON.stringify({names})}));
}

type CatalogNameConfirmation = {saved:true;item:CatalogName;position:number};
export function saveCatalogNames(data:{name:string},brandId?:number):Promise<CatalogNameConfirmation>;
export function saveCatalogNames(data:{names:string[]},brandId?:number):Promise<{saved:true}>;
export async function saveCatalogNames(data:{name:string}|{names:string[]},brandId?:number):Promise<{saved:true}|CatalogNameConfirmation> {
    const order='names' in data;
    const path=brandId==null?'brands':'product-series';
    const query=brandId==null?'':`?brand_id=${brandId}`;
    const res=await doFetch(`${BASE}/${path}${order?'/order':''}${query}`,{
        method:order?'PUT':'POST',headers:authHeaders({'Content-Type':'application/json'}),body:JSON.stringify(data),
    });
    const result=await handle<CatalogNameConfirmation>(res);
    if(!result.saved)throw new Error('保存未确认，请刷新后重试');
    return result;
}

export interface SupplierProductPage { sections?: {key:string;title:string;count:number;start:number}[]; products: SupplierProduct[]; page?: number; pages?: number; total?: number }
export async function listSupplierProducts(supplierId?: number, options?: { page?: number; per_page?: number; q?:string; sort?:string; status?:string; packaging_status?:string; category?:string; group_by?:string; signal?: AbortSignal }): Promise<SupplierProductPage> {
    const q = new URLSearchParams({view:'summary',...productSortRates(options?.sort)});
    if(options?.q)q.set('q',options.q);
    if(options?.sort)q.set('sort',options.sort);
    if(options?.status)q.set('status',options.status);
    if(options?.packaging_status)q.set('packaging_status',options.packaging_status);
    if(options?.category)q.set('category',options.category);
    if(options?.group_by)q.set('group_by',options.group_by);
    if (supplierId != null) q.set('supplier_id', String(supplierId));
    if (options?.page) q.set('page', String(options.page));
    if (options?.per_page) q.set('per_page', String(options.per_page));
    const url = `${BASE}/products${q.toString() ? `?${q}` : ''}`;
    return readQuery(`raw|${url}`, async signal => handle<SupplierProductPage>(await doFetch(url, { headers: authHeaders(), signal })), options);
}

export async function getSupplierProduct(id: number, supplierId?: number, signal?: AbortSignal): Promise<SupplierProduct> {
    const query = `?view=editor${supplierId == null ? '' : `&supplier_id=${supplierId}`}`;
    const url = `${BASE}/products/${id}${query}`;
    return readQuery(`raw|${url}`, async signal => handle<SupplierProduct>(await doFetch(url, {headers:authHeaders(),signal})), {signal});
}

export interface SupplierProductInput {
    name?: string | null;
    supply_price?: number | null;
    retail_price?: number | null;
    supplier_sku?: string | null;
    internal_size_cm?: { length?: number | null; width?: number | null; height?: number | null };
    external_size_cm?: { length?: number | null; width?: number | null; height?: number | null };
    weight?: number | null;
    attributes?: SupplierProduct['attributes'];
    attribute_changes?: Record<string,string|string[]|null>;
    product_labels?: string[];
    product_note?: string | null;
    /** 按名字解析：没有这个品牌就新建一条，与 App 共用 product_brands。 */
    brand_name?: string | null;
    purchase_url?: string | null;
    client_id?: string;
    // legacy flat aliases still accepted by the backend
    ext_length?:number|null; ext_width?:number|null; ext_height?:number|null;
    price?: number | null;
    height?: number | null; width?: number | null; depth?: number | null;
}

export async function updateSupplierProduct(
    id: number,
    data: SupplierProductInput,
    saveKey?:string,
): Promise<SupplierProduct> {
    const res = await doFetch(`${BASE}/products/${id}/fields`, {
        method: 'PATCH',
        headers: authHeaders({ 'Content-Type': 'application/json',...(saveKey?{'X-Product-Save-Key':saveKey}:{}) }),
        body: JSON.stringify(data),
    });
    return handle<SupplierProduct>(res);
}

export async function deleteSupplierProduct(id: number): Promise<{ deleted: boolean; id: number }> {
    const res = await doFetch(`${BASE}/products/${id}`, {
        method: 'DELETE',
        headers: authHeaders(),
    });
    return handle<{ deleted: boolean; id: number }>(res);
}

export async function deleteSupplierProducts(ids:number[]):Promise<{deleted:boolean;ids:number[]}> {
    const res=await doFetch(`${BASE}/products/batch-delete`,{method:'POST',headers:authHeaders({'Content-Type':'application/json'}),body:JSON.stringify({ids})});
    return handle<{deleted:boolean;ids:number[]}>(res);
}

// ── Team ────────────────────────────────────────────────────────────────────
export async function listSuppliersForTeam(options?: {signal?:AbortSignal;page?:number;kind?:string;q?:string;sort?:string}):Promise<{suppliers:SupplierSummary[];total:number;pages:number;counts:Record<string,number>}> {
    const query=new URLSearchParams({view:'summary',page:String(options?.page ?? 1),per_page:'24'});
    if(options?.sort)query.set('sort',options.sort);if(options?.kind)query.set('kind',options.kind);if(options?.q)query.set('q',options.q);
    const url=`${BASE}/suppliers?${query}`;
    return readQuery(`raw|${url}`,async signal=>handle(await doFetch(url,{headers:authHeaders(),signal})),options);
}
export async function getSupplierForTeam(id:number, signal?:AbortSignal):Promise<SupplierSummary> {
    const url = `${BASE}/suppliers/${id}`;
    return readQuery(`raw|${url}`, async signal => handle<SupplierSummary>(await doFetch(url,{headers:authHeaders(),signal})), {signal});
}

// Team-only: create a supplier (name + optional 访问码).
export async function teamCreateSupplier(maintenanceMode:SupplierKind='withpot'):Promise<SupplierSummary> {
    return handle<SupplierSummary>(await doFetch(`${BASE}/suppliers`,{method:'POST',headers:authHeaders({'Content-Type':'application/json'}),body:JSON.stringify({maintenance_mode:maintenanceMode})}));
}

// Team-only: rename a supplier and/or set/reset their 访问码 (empty code clears it).
export async function teamUpdateSupplier(id:number,data:SupplierChanges):Promise<Partial<SupplierSummary>&{id:number}> {
    return handle(await doFetch(`${BASE}/suppliers/${id}`,{method:'PATCH',headers:authHeaders({'Content-Type':'application/json'}),body:JSON.stringify(data)}));
}
export async function teamDeleteSupplier(id:number):Promise<{id:number;deleted:true}> {
    return handle(await doFetch(`${BASE}/suppliers/${id}`,{method:'DELETE',headers:authHeaders()}));
}
export async function uploadSupplierAvatar(id:number,file:File):Promise<{id:number;avatar_url:string}> {
    const body=new FormData();body.set('photo',await prepareSupplierAvatar(file));
    return handle(await doFetch(`${BASE}/suppliers/${id}/avatar`,{method:'POST',headers:authHeaders(),body:await directMediaForm(`${BASE}/suppliers/${id}/avatar`,body)}));
}
export async function settleSupplierCreation(id:number,token:string,retain=false):Promise<void> {
    await handle(await doFetch(`${BASE}/suppliers/${id}/${retain?'retain-creation':'discard-empty'}`,{method:'POST',headers:authHeaders({'Content-Type':'application/json'}),body:JSON.stringify({token})}));
}

export async function teamLogin(account:string):Promise<LoginResult> {
    return handle(await doFetch(`${BASE}/team-login`,{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({account})}));
}

export async function settleSupplierProductCreation(id:number, token:string, retain=false):Promise<{discarded?:boolean}> {
    return handle(await doFetch(`${BASE}/products/${id}/${retain?'retain-creation':'discard-empty'}`, {method:'POST',headers:authHeaders({'Content-Type':'application/json'}),body:JSON.stringify({token})}));
}

export async function saveSupplierProductMedia(id:number, form:FormData,saveKey?:string):Promise<SupplierProduct> {
    return handle(await doFetch(`${BASE}/products/${id}/media`,{method:'PATCH',headers:authHeaders(saveKey?{'X-Product-Save-Key':saveKey}:{}),body:await directMediaForm(`${BASE}/products/${id}/media`,form)}));
}


export interface SupplierProfileOptions {plants:string[];styles:string[];channels:string[];banks:string[]}
export interface SupplierRegion {id:string;country:string;city:string;detail:string;country_code:string;region:string;provider:string;place_id:string}
export interface SupplierBankBranch {id:number;branch_name:string;branch_code:string;city?:string|null}
export async function getSupplierProfileOptions(signal?:AbortSignal):Promise<SupplierProfileOptions> {
    return handle(await doFetch(`${BASE}/profile-options`,{headers:authHeaders(),signal}));
}
export async function searchSupplierLocations(q:string,signal?:AbortSignal):Promise<{locations:SupplierRegion[]}> {
    return handle(await doFetch(`${BASE}/locations?${new URLSearchParams({q})}`,{headers:authHeaders(),signal}));
}
export async function identifySupplierBank(account:string,signal?:AbortSignal):Promise<{bank_name:string|null;bank_code:string|null}> {
    return handle(await doFetch(`${BASE}/bank-card`,{method:'POST',headers:authHeaders({'Content-Type':'application/json'}),body:JSON.stringify({account}),signal}));
}
export async function searchSupplierBankBranches(bankName:string,q:string,offset=0,signal?:AbortSignal):Promise<{branches:SupplierBankBranch[];has_more:boolean;next_offset:number|null}> {
    return handle(await doFetch(`${BASE}/bank-branches?${new URLSearchParams({bank_name:bankName,q,offset:String(offset),limit:'20'})}`,{headers:authHeaders(),signal}));
}

export async function searchSupplierAddresses(q:string,signal?:AbortSignal,context=''):Promise<{addresses:Array<{address:string;name:string;provider:string}>}> {
    return handle(await doFetch(`${BASE}/addresses?${new URLSearchParams({q,context})}`,{headers:authHeaders(),signal}));
}

export type BrandAttributeProfile={id:number;name:string;english_name?:string|null;story?:string|null;attribute_defaults:Record<string,string|string[]|null>;attribute_overrides:Record<string,string|string[]|null>};
export async function getBrandAttributeProfile(brand:number|string,series?:string,signal?:AbortSignal):Promise<BrandAttributeProfile>{
    const query=new URLSearchParams({[typeof brand==='number'?'brand_id':'brand_name']:String(brand),...(series?{series}:{})});
    return handle(await doFetch(`${BASE}/brand-profile?${query}`,{headers:authHeaders(),signal}));
}
export async function saveBrandAttributeProfile(brand:number,changes:Record<string,string|string[]|null>,inherit:string[]=[],series?:string):Promise<void>{
    const query=new URLSearchParams({brand_id:String(brand),...(series?{series}:{})});
    await handle(await doFetch(`${BASE}/brand-profile?${query}`,{method:'PATCH',headers:authHeaders({'Content-Type':'application/json'}),body:JSON.stringify({changes,inherit})}));
}

export async function saveBrandPresentation(brand:number,changes:{english_name?:string|null;story?:string|null}):Promise<{id:number;english_name?:string|null;story?:string|null}>{
    return handle(await doFetch(`${BASE}/brand-profile?brand_id=${brand}`,{method:'PATCH',headers:authHeaders({'Content-Type':'application/json'}),body:JSON.stringify(changes)}));
}
